/** Overlay box geometry — estimating, clamping and migrating caption/narration boxes. */
import { CaptionAlign, CaptionOverlay, NarrationOverlay } from './types';
import { DEFAULT_CAPTION_BOX, DEFAULT_NARRATION_BOX } from './constants';

export function estimateCaptionBox(text: string): { bgWidth: number; bgHeight: number } {
  const len = text.trim().length;
  const bgWidth = Math.max(24, Math.min(58, 20 + len * 1.1));
  return { bgWidth, bgHeight: DEFAULT_CAPTION_BOX.bgHeight };
}

export function estimateNarrationBox(text: string): { bgWidth: number; bgHeight: number } {
  const len = text.trim().length;
  const lines = Math.max(1, Math.min(4, Math.ceil(len / 34)));
  const bgWidth = Math.max(34, Math.min(72, 30 + Math.min(len, 60) * 0.55));
  const bgHeight = Math.max(9, Math.min(20, 7 + lines * 2));
  return { bgWidth, bgHeight };
}

/**
 * Wraps caption text the way the browser will draw it, using the same font the preview uses.
 *
 * The renderer cannot measure text — FFmpeg has no metrics — so it otherwise guesses with an
 * average glyph width and the result drifts from the preview. Measuring here and sending the
 * resolved lines makes the burn match what the author saw.
 *
 * `boxWidthPct` is null for an auto-sized plate, which grows to fit; a resized plate is a hard
 * limit, so the font is stepped down until the text fits inside it.
 */
export function measureOverlayLayout(
  text: string,
  style: { fontFamily?: string; fontSizePct?: number; bold?: boolean; italic?: boolean },
  frame: { width: number; height: number },
  boxWidthPct: number | null,
  boxHeightPct: number | null,
): { lines: string[]; resolvedFontPct: number } | null {
  const clean = (text || '').trim();
  if (!clean || frame.width <= 0 || frame.height <= 0) return null;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const family = style.fontFamily || 'Inter';
  const startPct = typeof style.fontSizePct === 'number' && style.fontSizePct > 0 ? style.fontSizePct : 4;
  // An auto plate wraps at a readable width rather than running the full frame.
  const boxWidthPx = frame.width * ((boxWidthPct ?? 70) / 100);
  // The same padding the renderer reserves inside the plate (`capPad`), so the text budget here
  // matches the space it will actually be drawn into. A flat percentage was not enough: on a
  // small plate the padding is most of the difference, and the text overflowed by a few pixels.
  const padPx = Math.max(8, boxWidthPx * 0.06);
  const maxWidthPx = Math.max(24, boxWidthPx - padPx * 2);
  const maxHeightPx = boxHeightPct != null
    ? Math.max(12, frame.height * (boxHeightPct / 100) - padPx * 2)
    : Infinity;

  const wrapAt = (fontPx: number): string[] => {
    ctx.font = `${style.italic ? 'italic ' : ''}${style.bold ? 700 : 500} ${fontPx}px '${family}', sans-serif`;
    const words = clean.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let current = '';
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidthPx || !current) {
        current = candidate;
      } else {
        lines.push(current);
        current = word;
      }
    }
    if (current) lines.push(current);
    return lines;
  };

  let pct = startPct;
  let lines = wrapAt((frame.height * pct) / 100);
  // Shrink to fit a plate the author sized; never below a readable floor.
  while (boxHeightPct != null && pct > 1.2) {
    const fontPx = (frame.height * pct) / 100;
    if (lines.length * fontPx * 1.25 <= maxHeightPx) break;
    pct = Math.round((pct - 0.2) * 100) / 100;
    lines = wrapAt((frame.height * pct) / 100);
  }

  return { lines, resolvedFontPct: pct };
}

export function clampOverlaySize(bgWidth: number, bgHeight: number): { bgWidth: number; bgHeight: number } {
  return {
    bgWidth: Math.max(18, Math.min(95, bgWidth)),
    bgHeight: Math.max(4, Math.min(35, bgHeight)),
  };
}

export function clampOverlayPosition(x: number, y: number, align: CaptionAlign, bgWidth: number, bgHeight: number): { x: number; y: number } {
  let minX = 0;
  let maxX = 100;

  if (align === 'left') {
    minX = 0;
    maxX = 100 - bgWidth;
  } else if (align === 'center') {
    minX = bgWidth / 2;
    maxX = 100 - bgWidth / 2;
  } else {
    minX = bgWidth;
    maxX = 100;
  }

  const clampedY = Math.max(0, Math.min(100 - bgHeight, y));
  const clampedX = Math.max(minX, Math.min(maxX, x));
  return { x: clampedX, y: clampedY };
}

export function normalizeNarrationOverlay(overlay: NarrationOverlay | null, narrationText: string): NarrationOverlay | null {
  if (!overlay) return null;

  let rawWidth = Number.isFinite(overlay.bgWidth) ? overlay.bgWidth : DEFAULT_NARRATION_BOX.bgWidth;
  let rawHeight = Number.isFinite(overlay.bgHeight) ? overlay.bgHeight : DEFAULT_NARRATION_BOX.bgHeight;
  const estimated = estimateNarrationBox(narrationText);

  // Migrate legacy and prior untouched defaults to content-sized narration box.
  if (Math.abs(rawWidth - 74) < 0.001 && (Math.abs(rawHeight - 20) < 0.001 || Math.abs(rawHeight - 16) < 0.001 || Math.abs(rawHeight - 12) < 0.001)) {
    rawWidth = estimated.bgWidth;
    rawHeight = estimated.bgHeight;
  }
  if (Math.abs(rawWidth - 62) < 0.001 && Math.abs(rawHeight - 12) < 0.001) {
    rawWidth = estimated.bgWidth;
    rawHeight = estimated.bgHeight;
  }

  const size = clampOverlaySize(rawWidth, rawHeight);
  const align = overlay.align || 'center';
  const x = Number.isFinite(overlay.x) ? overlay.x : 50;
  const y = Number.isFinite(overlay.y) ? overlay.y : 68;
  const clampedPos = clampOverlayPosition(x, y, align, size.bgWidth, size.bgHeight);

  return {
    ...overlay,
    align,
    ...size,
    ...clampedPos,
  };
}

export function normalizeCaptionOverlay(overlay: CaptionOverlay | null): CaptionOverlay | null {
  if (!overlay) return null;

  let rawWidth = Number.isFinite(overlay.bgWidth) ? overlay.bgWidth : DEFAULT_CAPTION_BOX.bgWidth;
  let rawHeight = Number.isFinite(overlay.bgHeight) ? overlay.bgHeight : DEFAULT_CAPTION_BOX.bgHeight;
  const estimated = estimateCaptionBox(overlay.text || '');

  // Migrate legacy and prior untouched caption defaults to content-sized caption box.
  if (Math.abs(rawWidth - 62) < 0.001 && (Math.abs(rawHeight - 14) < 0.001 || Math.abs(rawHeight - 10) < 0.001)) {
    rawWidth = estimated.bgWidth;
    rawHeight = estimated.bgHeight;
  }
  if (Math.abs(rawWidth - 52) < 0.001 && Math.abs(rawHeight - 10) < 0.001) {
    rawWidth = estimated.bgWidth;
    rawHeight = estimated.bgHeight;
  }

  const size = clampOverlaySize(rawWidth, rawHeight);
  const align = overlay.align || 'center';
  const x = Number.isFinite(overlay.x) ? overlay.x : 50;
  const y = Number.isFinite(overlay.y) ? overlay.y : 82;
  const clampedPos = clampOverlayPosition(x, y, align, size.bgWidth, size.bgHeight);

  return {
    ...overlay,
    align,
    ...size,
    ...clampedPos,
  };
}
